import { Link } from 'react-router-dom'
import type { ProductSummary } from '@/api'
import { cn } from '@/lib/cn'
import { formatSoldCount } from '@/lib/format'
import { routes } from '@/routes'
import { AddPurchaseButton } from './AddPurchaseButton'
import { Price } from './Price'
import { ProductBadges } from './ProductBadges'
import { ShareButton } from './ShareButton'

/**
 * `shelf`   — carrossel da home: sem moldura, imagem grande, preço compacto.
 * `grid`    — resultados de busca: card com borda, selos e chip de desconto.
 * `compact` — lista de comprados: só imagem, título e preço.
 */
type Variant = 'shelf' | 'grid' | 'compact'

interface ProductCardProps {
  product: ProductSummary
  variant?: Variant
  className?: string
}

export function ProductCard({
  product,
  variant = 'shelf',
  className,
}: ProductCardProps) {
  const path = routes.product(product.slug)
  const image = product.images[0]

  if (variant === 'grid') return <GridCard product={product} path={path} image={image} className={className} />
  if (variant === 'compact') return <CompactCard product={product} path={path} image={image} className={className} />
  return <ShelfCard product={product} path={path} image={image} className={className} />
}

/* ------------------------------------------------------------------ */

interface InnerProps {
  product: ProductSummary
  path: string
  image?: string
  className?: string
}

function ProductImage({ src, alt }: { src?: string; alt: string }) {
  if (!src) {
    return <div className="size-full rounded bg-surface-muted" aria-hidden="true" />
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="size-full object-contain transition-transform duration-200 group-hover:scale-[1.03]"
    />
  )
}

function ShelfCard({ product, path, image, className }: InnerProps) {
  return (
    <article
      className={cn(
        'group relative flex h-full flex-col rounded bg-surface p-3',
        className,
      )}
    >
      {/* Recuo = padding do card (12px) + 8px, para o botão não encostar no
          canto da imagem. O `absolute` é medido a partir da borda do card. */}
      <div className="absolute right-5 top-5 z-10">
        <AddPurchaseButton productId={product.id} productTitle={product.title} />
      </div>

      <Link to={path} className="flex flex-1 flex-col gap-3">
        <div className="aspect-square w-full overflow-hidden">
          <ProductImage src={image} alt={product.title} />
        </div>

        {/*
        `h-[2lh]` = exatamente duas linhas. Reserva a altura para os preços
        ficarem alinhados na fileira e garante que o corte caia no fim de uma
        linha mesmo onde o `line-clamp` não pega (ver nota em global.css).
      */}
      <h3 className="line-clamp-2 h-[2lh] text-[13px] leading-tight text-ink-soft transition-colors group-hover:text-brand">
          {product.title}
        </h3>

        <div className="mt-auto">
          <Price
            value={product.price}
            originalValue={product.originalPrice}
            discountPercent={product.discountPercent}
            size="md"
          />
        </div>
      </Link>

      <div className="mt-2 flex justify-end">
        <ShareButton title={product.title} path={path} />
      </div>
    </article>
  )
}

function GridCard({ product, path, image, className }: InnerProps) {
  const sold = formatSoldCount(product.soldCount)

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col rounded border border-line bg-surface p-3.5',
        className,
      )}
    >
      {/* Recuo = padding do card (14px) + 8px, para o botão não encostar no
          canto da imagem. */}
      <div className="absolute right-[22px] top-[22px] z-10">
        <AddPurchaseButton productId={product.id} productTitle={product.title} />
      </div>

      <Link to={path} className="flex flex-1 flex-col gap-3">
        <div className="aspect-square w-full overflow-hidden">
          <ProductImage src={image} alt={product.title} />
        </div>

        {/*
        `h-[2lh]` = exatamente duas linhas. Reserva a altura para os preços
        ficarem alinhados na fileira e garante que o corte caia no fim de uma
        linha mesmo onde o `line-clamp` não pega (ver nota em global.css).
      */}
      <h3 className="line-clamp-2 h-[2lh] text-[13px] leading-tight text-ink-soft transition-colors group-hover:text-brand">
          {product.title}
        </h3>

        {sold && <p className="-mt-1 text-[11px] text-ink-faint">{sold}</p>}

        <div className="mt-auto">
          <Price
            value={product.price}
            originalValue={product.originalPrice}
            discountPercent={product.discountPercent}
            size="lg"
            discountStyle="chip"
          />
        </div>
      </Link>

      <div className="mt-3 flex items-center justify-between gap-2">
        <ProductBadges badges={product.badges} />
        <ShareButton title={product.title} path={path} className="ml-auto" />
      </div>
    </article>
  )
}

function CompactCard({ product, path, image, className }: InnerProps) {
  return (
    <article
      className={cn(
        'group relative flex h-full flex-col rounded border border-line bg-surface p-3',
        className,
      )}
    >
      {/* Recuo = padding do card (12px) + 8px, para o botão não encostar no
          canto da imagem. */}
      <div className="absolute right-5 top-5 z-10">
        <AddPurchaseButton productId={product.id} productTitle={product.title} size="sm" />
      </div>

      <Link to={path} className="flex flex-1 flex-col gap-3">
        <div className="aspect-square w-full overflow-hidden">
          <ProductImage src={image} alt={product.title} />
        </div>
        <h3 className="line-clamp-3 h-[3lh] text-[13px] leading-tight text-ink-soft transition-colors group-hover:text-brand">
          {product.title}
        </h3>
        <div className="mt-auto">
          <Price value={product.price} size="lg" />
        </div>
      </Link>
    </article>
  )
}

/* ------------------------------------------------------------------ */

/** Esqueleto com a mesma altura do card real, para não pular o layout. */
export function ProductCardSkeleton({ variant = 'shelf' }: { variant?: Variant }) {
  return (
    <div
      className={cn(
        'flex h-full flex-col gap-3 rounded bg-surface p-3',
        variant === 'grid' && 'border border-line p-3.5',
      )}
    >
      <div className="skeleton aspect-square w-full rounded" />
      <div className="skeleton h-3 w-full rounded" />
      <div className="skeleton h-3 w-2/3 rounded" />
      <div className="skeleton mt-auto h-6 w-1/2 rounded" />
    </div>
  )
}
